import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, Calendar, DollarSign, 
  Download, FileText, ChevronDown, ChevronUp, CheckCircle,
  XCircle, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = getBackendUrl();

const AdminStatistics = () => {
  const [overview, setOverview] = useState(null);
  const [eventStats, setEventStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const [overviewRes, eventsRes] = await Promise.all([
        fetch(`${API}/admin/statistics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/statistics/events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const overviewData = await overviewRes.json();
      const eventsData = await eventsRes.json();
      
      if (overviewData.success) {
        setOverview(overviewData.statistics);
      }
      if (eventsData.success) {
        setEventStats(eventsData.events);
      }
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (eventId = null) => {
    setExporting(true);
    try {
      let url = `${API}/admin/statistics/export/csv`;
      if (eventId) url += `?event_id=${eventId}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async (eventId = null) => {
    setExporting(true);
    try {
      let url = `${API}/admin/statistics/export/pdf`;
      if (eventId) url += `?event_id=${eventId}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `statistics_report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('PDF exported successfully');
      } else {
        toast.error('Failed to export PDF');
      }
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'text-[#c5bfa9]' }) => (
    <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#8b9a7e]">{label}</p>
          <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
          {subValue && <p className="text-xs text-[#6b7a5e] mt-1">{subValue}</p>}
        </div>
        <Icon className="w-8 h-8 text-[#3d4a35]" />
      </div>
    </div>
  );

  const ProgressBar = ({ value, max, color = 'bg-[#5a6f4e]' }) => (
    <div className="w-full h-2 bg-[#1a1f16] rounded-full overflow-hidden">
      <div 
        className={`h-full ${color} transition-all`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#1a1f16] min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Event Statistics</h1>
          <p className="text-[#8b9a7e] mt-1">Revenue, bookings, and performance metrics</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => handleExportCSV()}
            disabled={exporting}
            variant="outline"
            className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#2d3728]"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => handleExportPDF()}
            disabled={exporting}
            className="bg-[#4a5d42] hover:bg-[#5a6f4e] text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            icon={DollarSign}
            label="Total Revenue"
            value={`£${overview.total_revenue.toLocaleString()}`}
            color="text-green-400"
          />
          <StatCard 
            icon={Users}
            label="Total Bookings"
            value={overview.total_bookings}
            subValue={`${overview.total_customers} unique customers`}
          />
          <StatCard 
            icon={TrendingUp}
            label="Average Fill Rate"
            value={`${overview.average_fill_rate}%`}
          />
          <StatCard 
            icon={CheckCircle}
            label="Check-in Rate"
            value={`${overview.average_check_in_rate}%`}
            subValue={`${overview.total_checked_in} checked in`}
          />
        </div>
      )}

      {/* Monthly Charts */}
      {overview && overview.bookings_by_month?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Bookings Chart */}
          <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
            <h3 className="text-lg font-semibold text-[#c5bfa9] mb-4">Bookings by Month</h3>
            <div className="flex items-end gap-2 h-40">
              {overview.bookings_by_month.map((item, idx) => {
                const maxCount = Math.max(...overview.bookings_by_month.map(b => b.count));
                const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                const [year, month] = item.month.split('-');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthLabel = monthNames[parseInt(month, 10) - 1] || month;
                const showYear = idx === 0 || overview.bookings_by_month[idx - 1]?.month.split('-')[0] !== year;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center min-w-[40px]">
                    <span className="text-xs text-[#8b9a7e] mb-1">{item.count}</span>
                    <div 
                      className="w-full bg-[#5a6f4e] rounded-t transition-all hover:bg-[#6b8f5e]"
                      style={{ height: `${Math.max(height, 8)}%`, minHeight: '8px' }}
                      title={`${item.count} bookings in ${monthLabel} ${year}`}
                    />
                    <span className="text-xs text-[#6b7a5e] mt-2 whitespace-nowrap">
                      {monthLabel}{showYear ? ` '${year.slice(-2)}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
            <h3 className="text-lg font-semibold text-[#c5bfa9] mb-4">Revenue by Month</h3>
            <div className="flex items-end gap-2 h-40">
              {overview.revenue_by_month.map((item, idx) => {
                const maxRevenue = Math.max(...overview.revenue_by_month.map(r => r.revenue));
                const height = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;
                const [year, month] = item.month.split('-');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthLabel = monthNames[parseInt(month, 10) - 1] || month;
                const showYear = idx === 0 || overview.revenue_by_month[idx - 1]?.month.split('-')[0] !== year;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center min-w-[40px]">
                    <span className="text-xs text-green-400 mb-1">£{item.revenue.toFixed(0)}</span>
                    <div 
                      className="w-full bg-green-600 rounded-t transition-all hover:bg-green-500"
                      style={{ height: `${Math.max(height, 8)}%`, minHeight: '8px' }}
                      title={`£${item.revenue.toFixed(2)} in ${monthLabel} ${year}`}
                    />
                    <span className="text-xs text-[#6b7a5e] mt-2 whitespace-nowrap">
                      {monthLabel}{showYear ? ` '${year.slice(-2)}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Top Events */}
      {overview && overview.top_events?.length > 0 && (
        <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6 mb-6">
          <h3 className="text-lg font-semibold text-[#c5bfa9] mb-4">Top Events by Revenue</h3>
          <div className="space-y-3">
            {overview.top_events.slice(0, 5).map((event, idx) => (
              <div key={event.event_id} className="flex items-center">
                <span className="w-6 text-[#6b7a5e] text-sm">{idx + 1}.</span>
                <span className="flex-1 text-[#c5bfa9] truncate">{event.event_name}</span>
                <span className="text-green-400 font-medium">£{event.revenue.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event Details */}
      <div className="bg-[#232921] rounded-lg border border-[#3d4a35]">
        <div className="p-4 border-b border-[#3d4a35]">
          <h3 className="text-lg font-semibold text-[#c5bfa9]">Event Breakdown</h3>
        </div>
        
        {eventStats.length === 0 ? (
          <div className="p-8 text-center text-[#6b7a5e]">No events found</div>
        ) : (
          <div className="divide-y divide-[#3d4a35]">
            {eventStats.map((event) => (
              <div key={event.event_id}>
                <button
                  onClick={() => setExpandedEvent(expandedEvent === event.event_id ? null : event.event_id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-[#2d3728] transition-colors"
                >
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-[#8b9a7e] mr-3" />
                    <div className="text-left">
                      <p className="text-[#c5bfa9] font-medium">{event.event_name}</p>
                      <p className="text-sm text-[#6b7a5e]">{event.event_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-green-400 font-medium">£{event.revenue.toFixed(2)}</p>
                      <p className="text-xs text-[#6b7a5e]">{event.total_booked}/{event.total_capacity} booked</p>
                    </div>
                    {expandedEvent === event.event_id ? (
                      <ChevronUp className="w-5 h-5 text-[#8b9a7e]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#8b9a7e]" />
                    )}
                  </div>
                </button>
                
                {expandedEvent === event.event_id && (
                  <div className="px-4 pb-4 bg-[#1a1f16]">
                    {/* Event Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-[#232921] rounded-lg">
                        <p className="text-xs text-[#8b9a7e]">Fill Rate</p>
                        <p className="text-lg font-bold text-[#c5bfa9]">{event.fill_rate}%</p>
                        <ProgressBar value={event.fill_rate} max={100} />
                      </div>
                      <div className="p-3 bg-[#232921] rounded-lg">
                        <p className="text-xs text-[#8b9a7e]">Check-in Rate</p>
                        <p className="text-lg font-bold text-[#c5bfa9]">{event.check_in_rate}%</p>
                        <ProgressBar value={event.check_in_rate} max={100} color="bg-blue-500" />
                      </div>
                      <div className="p-3 bg-[#232921] rounded-lg">
                        <p className="text-xs text-[#8b9a7e]">Cancellation Rate</p>
                        <p className="text-lg font-bold text-[#c5bfa9]">{event.cancellation_rate}%</p>
                        <ProgressBar value={event.cancellation_rate} max={100} color="bg-red-500" />
                      </div>
                      <div className="p-3 bg-[#232921] rounded-lg">
                        <p className="text-xs text-[#8b9a7e]">Checked In</p>
                        <p className="text-lg font-bold text-[#c5bfa9]">{event.total_checked_in}/{event.total_booked}</p>
                      </div>
                    </div>
                    
                    {/* Team Breakdown */}
                    <h4 className="text-sm font-medium text-[#8b9a7e] mb-2">Team Breakdown</h4>
                    <div className="space-y-2">
                      {event.teams.map((team) => (
                        <div key={team.team_id} className="flex items-center justify-between p-2 bg-[#232921] rounded-lg">
                          <span className="text-[#c5bfa9]">{team.team_name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-[#8b9a7e]">
                              {team.booked}/{team.capacity} ({team.fill_rate}%)
                            </span>
                            <span className="text-sm text-blue-400">
                              {team.checked_in} checked in
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Export for this event */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => handleExportCSV(event.event_id)}
                        disabled={exporting}
                        variant="outline"
                        size="sm"
                        className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#2d3728]"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Export CSV
                      </Button>
                      <Button
                        onClick={() => handleExportPDF(event.event_id)}
                        disabled={exporting}
                        variant="outline"
                        size="sm"
                        className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#2d3728]"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Export PDF
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStatistics;
